import React, { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TableContainer from "@mui/material/TableContainer";
import Stack from "@mui/material/Stack";
import SchedulerTimeTable from "./SchedulerTimeTable";
import Schedules from "./Schedules";
import SchedulerActions from "./SchedulerActions";
import ScheduleEntryForm from "./SchedulerEntry/ScheduleEntryForm";
import ErrorPageWithSnackbar from "../../Feedback/ErrorPageWithSnackbar";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import { selectMissionsRestSet, selectSelectedRoom } from "../../../store/room/room-selectors";
import { selectScheduleEntries } from "../../../store/room/scheduler/scheduler-selectors";
import { selectIsReadOnly } from "../../../store/app-configurations/app-configurations-selectors";
import { FilterKeys } from "../../../enums/query-params/filters";
import { errorResetResponse, isErrorSetOpen, isSuccessSetOpen, successResetResponse } from "../../../interfaces/api/rest-set";

export const hourPixelHeight = "48px";
export const timePixelWidth = "48px";
export const weekdayPixelHeight = "37.5px";

const Scheduler: React.FC = () => {
    const { t } = useTranslation();

    const [queryParams, setQueryParams] = useSearchParams();

    const dispatch = useSharedDispatch();
      
    const { patchMissionsRestSet } = useSharedActions();

    const roomData = useSharedSelector((state) => selectSelectedRoom(state));
    const isReadOnly = useSharedSelector((state) => selectIsReadOnly(state));
    const scheduleEntries = useSharedSelector((state) => selectScheduleEntries(state));
    const missionsRestSet = useSharedSelector((state) => selectMissionsRestSet(state));

    const queryMissionId = queryParams.get(FilterKeys.MissionId);

    const tableContainerRef = useRef<HTMLDivElement>(null);

    const [isNewEntryDialogOpen, setIsNewEntryDialogOpen] = useState(queryMissionId !== null);

    const newSchedulerEntryOpenClickHandler = () => {
        setIsNewEntryDialogOpen(true);
    };

    const newSchedulerEntryCloseHandler = () => {
        setIsNewEntryDialogOpen(false);
        setQueryParams((prevState) => {
            prevState.delete(FilterKeys.MissionId);
            return prevState;
        });
        if (isSuccessSetOpen(missionsRestSet)) {
            dispatch(patchMissionsRestSet(successResetResponse(missionsRestSet)));
        } else if (isErrorSetOpen(missionsRestSet)) {
            dispatch(patchMissionsRestSet(errorResetResponse(missionsRestSet)));
        }
    };

    if (scheduleEntries.rest.getError) {
        return <ErrorPageWithSnackbar errorTitle={scheduleEntries.rest.getError.message ?? t("error-schedule-loading")} />;
    }

    return (
      <React.Fragment>
        {roomData != null && (
          <Stack sx={{ height: "100%" }}>
            <SchedulerActions floorId={roomData.floorId} onNewSchedulerEntryClick={newSchedulerEntryOpenClickHandler} />
            <TableContainer
              ref={tableContainerRef}
              sx={(theme) => ({
                maxHeight: "100%",
                userSelect: "none",
                height: "100%",
                marginTop: theme.spacing(0.5),
                position: "relative",
                borderLeft: "0px",
                "&::-webkit-scrollbar": {
                  width: "0px",
                },
              })}
            >
              <div
                style={{
                  opacity: isReadOnly ? 0.75 : 1,
                  pointerEvents: isReadOnly ? "none" : "initial",
                }}
              >
                <SchedulerTimeTable tableContainerRef={tableContainerRef} floorId={roomData.floorId} />
                <div
                  style={{
                    position: "absolute",
                    left: timePixelWidth,
                    height: 24 * parseInt(hourPixelHeight, 10),
                    width: `calc(100% - ${timePixelWidth})`,
                    top: weekdayPixelHeight,
                    pointerEvents: "none",
                    zIndex: 1,
                  }}
                >
                  <div
                    style={{
                      position: "relative",
                      height: "100%",
                      width: "100%",
                    }}
                  >
                    <Schedules />
                  </div>
                </div>
              </div>
            </TableContainer>
          </Stack>
        )}

        {isNewEntryDialogOpen && roomData != null && (
          <ScheduleEntryForm
            roomId={roomData.id}
            linkedMissionId={queryMissionId ?? undefined}
            onDialogClose={newSchedulerEntryCloseHandler}
          />
        )}
      </React.Fragment>
    );
};

export default Scheduler;